import { useEffect, useCallback, useMemo } from 'react';

import { trpc, trpcQueryUtils } from '@web/lib/trpc';
import { router } from '@web/lib/react-router';

import { SignInCredentials } from '../types';
import { useSessionId } from './session-id';

import * as Sentry from '@sentry/react';

export function useAuthSession() {
  const [sessionId, setSessionId] = useSessionId();

  const { data, status: userRequestStatus } = trpc.auth.getProfile.useQuery(
    undefined,
    { enabled: !!sessionId },
  );

  const { mutateAsync: signOutAsync } = trpc.auth.logout.useMutation();

  const checkingStatus = !sessionId ? 'success' : userRequestStatus;
  const user = useMemo(() => (sessionId ? data : null), [data, sessionId]);

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        name: user.firstName + (user.lastName ? ` ${user.lastName}` : ''),
        email: user.email ?? '',
      });
    }
  }, [user]);

  const { mutateAsync: requestSignIn, reset } = trpc.auth.login.useMutation();

  const removeSessionId = useCallback(() => {
    setSessionId(null);
  }, [setSessionId]);

  const signIn = useCallback(
    async ({ email, password }: SignInCredentials) => {
      const { sessionId } = await requestSignIn({ email, password });

      if (sessionId) {
        setSessionId(sessionId);
        await trpcQueryUtils.auth.getProfile.invalidate();
        return true;
      }
      return false;
    },
    [requestSignIn, setSessionId],
  );

  const signOut = useCallback(async () => {
    await signOutAsync();
    reset();
    removeSessionId();
  }, [removeSessionId, reset, signOutAsync]);

  useEffect(() => {
    void router.invalidate();
  }, [user]);

  return { user, checkingStatus, signIn, signOut, removeSessionId };
}
